<template>
  <v-menu
    v-model="openMenu"
    max-width="400"
    location="right center"
    :close-on-content-click="false"
    :activator="openMenu ? positioningActivator || $el : menuActivator"
  >
    <template v-slot:activator="{ props: activatorProps }">
      <div v-bind="activatorProps" ref="menuActivator" >
        <slot></slot>
      </div>
    </template>
    <template v-slot:default>
      <v-card class="ml-1 pa-3" width="240">
        <div class="d-flex pb-2 align-center justify-space-between">
          <v-icon>mdi-list-status</v-icon>
          <span class="text-h7 font-weight-bold">Select fields</span>
          <v-btn
            variant="plain"
            size="small"
            icon="mdi-close"
            @click="openMenu = false"
          />
        </div>
        <v-divider />
        <v-list density="compact" class="pt-0">
          <entity-field-selector-fields
            :fields="fields"
            :depth="1"
            :maxDepth="maxDepth"
          />
        </v-list>
        <v-divider></v-divider>
        <div class="d-flex justify-center">
          <v-btn variant="plain" density="compact" @click="openMenu = false">
            Close
          </v-btn>
        </div>
      </v-card>
    </template>
  </v-menu>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import debounce from '@/utils/debounce'
import { DomainModelRole } from '@/constants/cardTypes'
import EntityFieldSelectorFields from '@/components/EntityFieldSelectorFields'

const { card, positioningActivator } = defineProps({
  card: Object,
  positioningActivator: Object
})
const emit = defineEmits(['onUpdateFields'])

const openMenu = ref(false)
const menuActivator = ref(null)

const store = useStore()
const entities = computed(() => store.state.workflow.workflow.schemas)
const isCommand = computed(() => card.cardType?.domainModelRole === DomainModelRole.COMMAND)
const maxDepth = 3

let pendingSave = false
const debouncedUpdateHandler = debounce((newValue) => {
  pendingSave = true
  emit('onUpdateFields', newValue)
}, 500)

const fields = ref([])
let isInitialized = false

const buildFieldsRecursive = (entityFields, schemaFields, depth) => {
  return entityFields.map((field) => {
    const schemaField = (schemaFields || []).find((df) => df.name === field.name)

    if (!field.relatedEntityId || depth >= maxDepth) {
      return {
        ...field,
        checked: !!schemaField
      }
    }

    const relatedEntity = entities.value?.find((entity) => entity.id === field.relatedEntityId)
    const childSchemaFields = schemaField?.fields || []

    return {
      ...field,
      checked: !!schemaField,
      entityFields: relatedEntity?.fields
        ? buildFieldsRecursive(relatedEntity.fields, childSchemaFields, depth + 1)
        : []
    }
  })
}

const buildFields = () => {
  let entityId
  if (isCommand.value) {
    entityId = card.entityId
  } else {
    entityId = card.schemaData.schema?.entityId
  }
  const rootEntity = entities.value?.find((entity) => entity.id === entityId)
  const rootEntityFields = rootEntity?.fields || []
  const schemaFields = card.schemaData.fields

  isInitialized = false
  fields.value = buildFieldsRecursive(rootEntityFields, schemaFields, 1)
}

watch(() => fields.value, (newVal) => {
  if (!isInitialized) {
    isInitialized = true

    return
  }

  debouncedUpdateHandler(newVal)
}, { deep: true })

watch(openMenu, (newVal) => {
  if (!newVal) {
    debouncedUpdateHandler.flush()
  }
})

watch(() => card, () => {
  if (pendingSave) {
    pendingSave = false

    return
  }

  debouncedUpdateHandler.cancel()
  buildFields()
}, { immediate: true, deep: true })
</script>

<style scoped>
.v-list-item {
  padding: 0 !important;
}

.v-list-item--density-compact:not(.v-list-item--nav).v-list-item--one-line {
  padding-inline: 0;
}

.v-list-group__items .v-list-item {
  padding-inline-start: 16px !important;
}

.v-list-item--density-compact.v-list-item--one-line {
  min-height: 28px;
  &.v-list-item--prepend {
    direction: rtl;
    text-align: left;
  }
}

.v-list-group__items .v-list-item--density-compact.v-list-item--one-line {
  direction: ltr;
}

::v-deep(.v-list-item__append .v-list-item__spacer) {
  width: 8px !important;
}

.v-input--density-compact {
  --v-input-control-height: 28px;
}

.w-50 {
  width: 50%;
}
</style>
